import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import { AXIOS_API_INSTANCE } from '../Constants';
import { Box, Checkbox, Chip, FormControl, FormControlLabel, IconButton, InputLabel, Link, ListItemText, MenuItem, OutlinedInput, Select, Tooltip } from '@mui/material';
import Grid from "@mui/material/Grid2"
import { send_authorized_request } from '../Constants/cognito';
import { useSnackbar } from 'notistack';
import dayjs from "dayjs"
import { findLocation, useLocations } from './ReactQuery/QueryLocations';
import { LoadingBackdropIndeterminate } from './LoadingBackdrop';
import ConfirmModal from './modals/ConfirmModal';
import { useTrackedUser } from './UserState';

const tooltip_style = {
    tooltip: {
        sx: {
            bgcolor: 'common.black',
            '& .MuiTooltip-arrow': {
                color: 'common.black',
            },
        },
    },
}

const roles = [
    {
        role: 'permissions_user_admin',
        tooltip: 'Allows the user to create, modify, and delete users as well as alter their permissions',
        label: 'User Admin'
    },
    {
        role: 'permissions_config_admin',
        tooltip: 'Allows the user to create, modify, and delete all aspects of the configuration resources such as fees, rental rates/groups, rental categories, and rental types',
        label: 'Configuration Admin'
    },
    {
        role: 'permissions_inventory_admin',
        tooltip: 'Allows the user to create, modify, and delete rental items and associated notes',
        label: 'Inventory Admin',
        client_attribute: 'has_inventory'
    },
    {
        role: 'permissions_service_tech',
        tooltip: 'Allows the user to manage and work on Service Orders',
        label: 'Service Technician',
        client_attribute: 'has_service'
    },
    {
        role: 'permissions_service_writer',
        tooltip: 'Allows the user to create and modify Service Orders and take payments',
        label: 'Service Writer',
        client_attribute: 'has_service'
    },
    {
        role: 'permissions_service_manager',
        tooltip: 'Allows the user to create, modify, and delete all things related to Service Orders',
        label: 'Service Manager',
        client_attribute: 'has_service'
    },
    {
        role: 'permissions_service_parts_specialist',
        tooltip: 'Allows the user to create, modify, and delete all things related to Service Order Parts',
        label: 'Service Parts Specialist',
        client_attribute: 'has_service'
    },
    {
        role: 'permissions_checkout_admin',
        tooltip: 'Allows the user to create, modify, and delete customers, active rentals, and invoices',
        label: 'Checkout Admin',
        client_attribute: 'has_rental'
    },
    {
        role: 'permissions_checkout_user',
        tooltip: 'Allows the user to create and modify customers, active rentals, and invoices',
        label: 'Checkout User',
        client_attribute: 'has_rental'
    },
    {
        role: 'permissions_reports_user',
        tooltip: 'Allows the user to view reports',
        label: 'Reports User'
    },

]

export default function UserModal({ user, open, closeModal, closeModalWithChanges, creatingNewUser }) {
    const { enqueueSnackbar } = useSnackbar();
    const { data: locations, } = useLocations();
    const [my_user,] = useTrackedUser();
    const [my_editing_user, setMyEditingUser] = React.useState(user || {});
    const [saving, setSaving] = React.useState(false)
    const [deleting, setDeleting] = React.useState(false)
    const [confirmation, setConfirmation] = React.useState({
        open: false,
        text: '',
        callback: null
    })
    React.useEffect(() => {
        const new_user = window.structuredClone(user);
        (user?.locations ?? []).forEach((loc) => {
            new_user.location_ids = (new_user.location_ids ?? []).concat([loc.id])
        })
        setMyEditingUser(new_user);
    }, [user]);
    console.log('RENDERING USER MODAL')

    const handleChange = (prop) => (event) => {
        const checkbox_attributes = ['permissions_user_admin',
            'permissions_config_admin',
            'permissions_inventory_admin',
            'permissions_service_tech',
            'permissions_service_writer',
            'permissions_service_manager',
            'permissions_service_parts_specialist',
            'permissions_checkout_admin',
            'permissions_checkout_user',
            'permissions_reports_user']
        if (checkbox_attributes.includes(prop)) {
            setMyEditingUser({ ...my_editing_user, [prop]: event.target.checked })
        } else {
            setMyEditingUser({ ...my_editing_user, [prop]: event.target.value })
        }
    };

    const save = (event) => {
        setSaving(true)
        if (creatingNewUser) {
            send_authorized_request(() => AXIOS_API_INSTANCE.put(`users`, my_editing_user).then(() => {
                enqueueSnackbar('Successfully created user', { variant: 'success' })
                setSaving(false)
                closeModalWithChanges(true)
            }).catch((error) => {
                enqueueSnackbar(`Failed to create user <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                setSaving(false)
            }))
        } else {
            send_authorized_request(() => AXIOS_API_INSTANCE.post(`users/${my_editing_user.id}`, my_editing_user).then(() => {
                enqueueSnackbar('Successfully updated user', { variant: 'success' })
                setSaving(false)
                closeModalWithChanges(true)
            }).catch((error) => {
                enqueueSnackbar(`Failed to update user <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                setSaving(false)
            }))
        }
    }

    const delete_user = (event) => {
        setConfirmation({
            open: true,
            text: `Are you sure you want to delete user ${my_editing_user.email}?`,
            callback: (confirmation) => {
                if (confirmation) {
                    setDeleting(true)
                    send_authorized_request(() =>
                        AXIOS_API_INSTANCE.delete(`users/${my_editing_user.id}`).then(() => {
                            enqueueSnackbar('Successfully deleted user', { variant: 'success' })
                            setDeleting(false)
                            closeModalWithChanges(true)
                        }).catch((error) => {
                            enqueueSnackbar(`Failed to delete user <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                            setDeleting(false)
                        })
                    )
                }
                setConfirmation({ text: '', open: false })
            }
        })
    }

    const resend_link = () => {
        setSaving(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`users/${my_editing_user.id}/resend_link`).then(() => {
                enqueueSnackbar('Successfully resent link', { variant: 'success' })
                setSaving(false)
                closeModalWithChanges(false)
            }).catch((error) => {
                enqueueSnackbar(`Failed to resend link <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                setSaving(false)
            })
        )
    }

    const deactivate_user = () => {
        setConfirmation({
            open: true,
            text: `Are you sure you want to deactivate user ${my_editing_user.email}?`,
            callback: (confirmation) => {
                if (confirmation) {
                    setSaving(true)
                    send_authorized_request(() =>
                        AXIOS_API_INSTANCE.post(`users/${my_editing_user.id}/deactivate`).then(() => {
                            enqueueSnackbar('Successfully deactivated user', { variant: 'success' })
                            setSaving(false)
                            closeModalWithChanges(true)
                        }).catch((error) => {
                            enqueueSnackbar(`Failed to deactivate user <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                            setSaving(false)
                        })
                    )
                }
                setConfirmation({ text: '', open: false })
            }
        })
    }

    const reactivate_user = () => {
        setConfirmation({
            open: true,
            text: `Are you sure you want to reactivate user ${my_editing_user.email}?`,
            callback: (confirmation) => {
                if (confirmation) {
                    setSaving(true)
                    send_authorized_request(() =>
                        AXIOS_API_INSTANCE.post(`users/${my_editing_user.id}/reactivate`).then(() => {
                            enqueueSnackbar('Successfully reactivated user', { variant: 'success' })
                            setSaving(false)
                            closeModalWithChanges(true)
                        }).catch((error) => {
                            enqueueSnackbar(`Failed to reactivate user <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                            setSaving(false)
                        })
                    )
                }
                setConfirmation({ text: '', open: false })
            }
        })
    }

    return (
        (<div>
            <Dialog
                open={open}
                onClose={closeModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                maxWidth='md'
            >
                <DialogTitle>
                    {creatingNewUser ? "Creating User" : "Modifying User"}
                    <IconButton
                        aria-label="close"
                        onClick={closeModal}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <LoadingBackdropIndeterminate
                        open={saving}
                        text={"Saving User"}
                    />
                    <LoadingBackdropIndeterminate
                        open={deleting}
                        text={"Deleting User"}
                    />
                    <Grid container columns={{ xs: 6, sm: 12 }}>
                        <Grid size={6} container direction="column" rowSpacing={1}>
                            <Grid>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    id="name"
                                    label="Name"
                                    type="text"
                                    fullWidth
                                    variant="standard"
                                    value={my_editing_user?.name || ''}
                                    onChange={handleChange('name')}
                                    sx={{ mt: 0 }}
                                />
                            </Grid>
                            <Grid>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    id="email"
                                    label="Email"
                                    type="text"
                                    fullWidth
                                    variant="standard"
                                    value={my_editing_user?.email || ''}
                                    onChange={handleChange('email')}
                                    required={true}
                                />
                            </Grid>
                            {!creatingNewUser &&
                                <Grid>
                                    <span><b>Last Logged In</b></span>
                                    <br />
                                    <span>{my_editing_user?.last_logged_in ? dayjs.utc(my_editing_user.last_logged_in).local().format('M/D/YYYY') : 'Never'}</span>
                                    {my_editing_user?.last_logged_in === null &&
                                        <span> (<Link href="#" onClick={resend_link}>Resend Link</Link>)</span>
                                    }
                                </Grid>
                            }
                            {!creatingNewUser &&
                                <Grid>
                                    <span><b>Created</b></span>
                                    <br />
                                    <span>{dayjs.utc(my_editing_user?.date_created).local().format('M/D/YYYY')}</span>
                                </Grid>
                            }
                            <Grid>
                                <FormControl sx={{ marginTop: 1, width: '100%' }}>
                                    <InputLabel id="multiple-chip-locations-label">Assigned Locations</InputLabel>
                                    <Select
                                        labelId="multiple-chip-locations-label"
                                        id="multiple-chip-locations"
                                        label="Assigned Locations"
                                        multiple
                                        value={my_editing_user?.location_ids || []}
                                        onChange={handleChange('location_ids')}
                                        input={<OutlinedInput id="select-multiple-chip" label="Assigned Locations" />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((value) => (
                                                    <Chip key={value} label={findLocation(locations, value)?.name} />
                                                ))}
                                            </Box>
                                        )}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 48 * 4.5 + 8,
                                                    width: 250,
                                                },
                                            },
                                        }}
                                    >
                                        {locations.map((loc) => (
                                            <MenuItem
                                                key={loc.id}
                                                value={loc.id}
                                            >
                                                <Checkbox checked={(my_editing_user?.location_ids ?? []).indexOf(loc.id) > -1} />
                                                <ListItemText primary={loc.name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid container justifyContent={'flex-end'} size={6}>
                            <Grid size={12}>
                                <span>Permissions</span>
                            </Grid>
                            {!creatingNewUser &&
                                <Grid size={10}>
                                    <Tooltip slotProps={tooltip_style} title="Full access to everything" arrow placement='left'>
                                        <FormControlLabel control={<Checkbox checked={my_editing_user?.root_admin || false} disabled />} label="Root Admin" labelPlacement='start' sx={{ display: 'flex', 'justify-content': 'space-between' }} />
                                    </Tooltip>
                                </Grid>
                            }
                            {roles.map((role) => {
                                if (role.client_attribute === undefined || my_user.client[role.client_attribute]) {
                                    return <Grid size={10} key={role.role}>
                                        <Tooltip slotProps={tooltip_style} title={role.tooltip} arrow placement='left'>
                                            <FormControlLabel control={<Checkbox checked={my_editing_user?.[role.role] || false} onChange={handleChange(role.role)} />} label={role.label} labelPlacement='start' sx={{ display: 'flex', 'justify-content': 'space-between' }} />
                                        </Tooltip>
                                    </Grid>
                                } else {
                                    return undefined
                                }
                            }

                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid size="auto">
                            {!creatingNewUser &&
                                <Grid container>
                                    <Grid>
                                        <Button disabled={my_editing_user?.root_admin} variant='outlined' color='error' onClick={delete_user} sx={{ mr: 1 }}>
                                            <DeleteIcon /> <Box sx={{ pl: 1 }}><span>Delete</span></Box>
                                        </Button>
                                    </Grid>
                                    <Grid>
                                        {my_editing_user?.deactivated ?
                                            <Button disabled={my_editing_user?.root_admin} variant='outlined' color='primary' onClick={reactivate_user} sx={{ mr: 1 }}>
                                                <PersonIcon /> <Box sx={{ pl: 1 }}><span>Reactivate</span></Box>
                                            </Button>
                                            :
                                            <Button disabled={my_editing_user?.root_admin} variant='outlined' color='error' onClick={deactivate_user} sx={{ mr: 1 }}>
                                                <PersonOffIcon /> <Box sx={{ pl: 1 }}><span>Deactivate</span></Box>
                                            </Button>
                                        }
                                    </Grid>
                                </Grid>
                            }
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" autoFocus onClick={save}>
                                {creatingNewUser ? "Create" : "Save"}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
                <ConfirmModal
                    {...confirmation}
                />
            </Dialog>
        </div>)
    );
}